import { useEffect, useRef } from "react";

import {
  Combobox,
  ComboboxData,
  ComboboxItem,
  defaultOptionsFilter,
  getOptionsLockup,
  getParsedComboboxData,
  isOptionsGroup,
  Pill,
  PillsInput,
  ScrollArea,
  useCombobox,
} from "@mantine/core";
import { useId, useUncontrolled } from "@mantine/hooks";

import { IconCheck } from "@tabler/icons-react";

import { inputDefaultProps } from "@/theme/mantineTheme";

/** Sentinel value for the creatable row; must not collide with real option values. */
const CREATE_OPTION_VALUE = "__multiselect_creatable_sentinel__";

function flattenComboboxItems(parsed: ReturnType<typeof getParsedComboboxData>): ComboboxItem[] {
  const out: ComboboxItem[] = [];
  for (const item of parsed) {
    if (isOptionsGroup(item)) {
      out.push(...item.items);
    } else {
      out.push(item);
    }
  }
  return out;
}

function shouldShowCreateOption(
  creatable: boolean | undefined,
  search: string,
  selected: string[],
  flatItems: ComboboxItem[],
): boolean {
  if (!creatable) return false;
  const trimmed = search.trim();
  if (!trimmed) return false;

  if (selected.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
    return false;
  }

  for (const option of flatItems) {
    if (
      String(option.value).toLowerCase() === trimmed.toLowerCase() ||
      String(option.label).toLowerCase() === trimmed.toLowerCase()
    ) {
      return false;
    }
  }

  return true;
}

export interface MultiSelectCreatableProps {
  label?: React.ReactNode;
  placeholder?: string;
  data: ComboboxData;
  required?: boolean;
  /** @default true */
  searchable?: boolean;
  creatable?: boolean;
  maxDropdownHeight?: number;
  nothingFoundMessage?: React.ReactNode;
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
  error?: React.ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  id?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  "data-testid"?: string;
}

export default function MultiSelectCreatable({
  label,
  placeholder,
  data,
  required,
  searchable = true,
  creatable,
  maxDropdownHeight = 220,
  nothingFoundMessage = "Nothing found",
  value,
  defaultValue,
  onChange,
  error,
  disabled,
  readOnly,
  name,
  id,
  onBlur,
  onFocus,
  onKeyDown,
  "data-testid": dataTestId,
}: MultiSelectCreatableProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const parsedData = getParsedComboboxData(data);
  const flatItems = flattenComboboxItems(parsedData);
  const optionsLockup = getOptionsLockup(parsedData);
  const retainedSelectedOptions = useRef<Record<string, ComboboxItem>>({});

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [_value, setValue] = useUncontrolled({
    value,
    defaultValue,
    finalValue: [] as string[],
    onChange,
  });

  const [_searchValue, setSearchValue] = useUncontrolled({
    finalValue: "",
  });

  const handleSearchChange = (next: string) => {
    setSearchValue(next);
    combobox.resetSelectedOption();
  };

  useEffect(() => {
    _value.forEach((val) => {
      const key = `${val}`;
      if (key in optionsLockup) {
        retainedSelectedOptions.current[key] = optionsLockup[key];
      }
    });
  }, [optionsLockup, _value]);

  const filteredFlat = searchable
    ? defaultOptionsFilter({
        options: flatItems,
        search: _searchValue,
        limit: Infinity,
      })
    : flatItems;

  const filteredSelectable = filteredFlat.filter(
    (item): item is ComboboxItem => !isOptionsGroup(item),
  );

  const trimmedSearch = _searchValue.trim();
  const showCreateOption = shouldShowCreateOption(creatable, _searchValue, _value, flatItems);

  const showNothingFound =
    searchable && trimmedSearch.length > 0 && filteredSelectable.length === 0 && !showCreateOption;

  const handleInputKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);

    if (event.key === " " && !searchable) {
      event.preventDefault();
      combobox.toggleDropdown();
    }

    if (event.key === "Backspace" && _searchValue.length === 0 && _value.length > 0) {
      setValue(_value.slice(0, _value.length - 1));
    }
  };

  const handleOptionSubmit = (valueToSubmit: string) => {
    const trimmedCreate = _searchValue.trim();

    if (valueToSubmit === CREATE_OPTION_VALUE) {
      if (
        creatable &&
        trimmedCreate &&
        shouldShowCreateOption(creatable, _searchValue, _value, flatItems)
      ) {
        setValue([..._value, trimmedCreate]);
        retainedSelectedOptions.current[trimmedCreate] = {
          value: trimmedCreate,
          label: trimmedCreate,
        };
      }
      handleSearchChange("");
      combobox.updateSelectedOptionIndex("selected");
      return;
    }

    const option = optionsLockup[valueToSubmit];
    if (!option || option.disabled) {
      handleSearchChange("");
      return;
    }

    const optionValue = String(option.value);
    if (_value.includes(optionValue)) {
      setValue(_value.filter((internalValue) => internalValue !== optionValue));
    } else {
      setValue([..._value, optionValue]);
    }

    handleSearchChange("");
    combobox.updateSelectedOptionIndex("selected");
  };

  const checkIconSize = 12;

  const pills = _value.map((item, index) => {
    const optionData = optionsLockup[`${item}`] || retainedSelectedOptions.current[`${item}`];
    const pillLabel = optionData?.label ?? item;

    return (
      <Pill
        disabled={disabled}
        key={`${item}-${index}`}
        withRemoveButton={!readOnly && !optionsLockup[`${item}`]?.disabled}
        onRemove={() => setValue(_value.filter((internalItem) => internalItem !== item))}
      >
        {pillLabel}
      </Pill>
    );
  });

  const dropdownHidden = disabled || readOnly;

  return (
    <>
      <Combobox
        store={combobox}
        readOnly={readOnly}
        __staticSelector="MultiSelect"
        onOptionSubmit={handleOptionSubmit}
      >
        <Combobox.DropdownTarget>
          <PillsInput
            radius="sm"
            id={inputId}
            label={label}
            error={error}
            disabled={disabled}
            required={required}
            pointer={!searchable}
            data-testid={dataTestId}
            size={inputDefaultProps.size}
            labelProps={{ id: `${inputId}-label` }}
            data-expanded={combobox.dropdownOpened || undefined}
            __defaultRightSection={<Combobox.Chevron size="sm" error={error} />}
            onClick={() => (searchable ? combobox.openDropdown() : combobox.toggleDropdown())}
            // Mantine sizes the multiline padding from these props, so they need the size too;
            // without it the pills sit at the top of the input instead of centered.
            __stylesApiProps={{
              size: inputDefaultProps.size,
              rightSectionPointerEvents: "none",
              multiline: true,
            }}
          >
            <Pill.Group disabled={disabled}>
              {pills}
              <Combobox.EventsTarget autoComplete="off">
                <PillsInput.Field
                  id={inputId}
                  disabled={disabled}
                  value={_searchValue}
                  pointer={!searchable}
                  placeholder={placeholder}
                  onKeyDown={handleInputKeydown}
                  readOnly={readOnly || !searchable}
                  type={!searchable && !placeholder ? "hidden" : "visible"}
                  onFocus={(event) => {
                    onFocus?.(event);
                    if (searchable) combobox.openDropdown();
                  }}
                  onBlur={(event) => {
                    onBlur?.(event);
                    combobox.closeDropdown();
                    handleSearchChange("");
                  }}
                  onChange={(event) => {
                    handleSearchChange(event.currentTarget.value);
                    if (searchable) combobox.openDropdown();
                  }}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>

        <Combobox.Dropdown hidden={dropdownHidden}>
          <Combobox.Options labelledBy={label ? `${inputId}-label` : undefined}>
            <ScrollArea.Autosize
              type="scroll"
              offsetScrollbars="y"
              mah={maxDropdownHeight}
              scrollbarSize="var(--combobox-padding)"
            >
              {filteredSelectable.map((item) => {
                const itemValue = String(item.value);
                const checked = _value.includes(itemValue);
                return (
                  <Combobox.Option
                    key={itemValue}
                    active={checked}
                    value={itemValue}
                    aria-selected={checked}
                    disabled={item.disabled}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {checked && <IconCheck size={checkIconSize} />}
                      <span>{item.label}</span>
                    </span>
                  </Combobox.Option>
                );
              })}
              {showCreateOption && (
                <Combobox.Option value={CREATE_OPTION_VALUE}>
                  + Create &quot;{trimmedSearch}&quot;
                </Combobox.Option>
              )}
            </ScrollArea.Autosize>
            {showNothingFound && <Combobox.Empty>{nothingFoundMessage}</Combobox.Empty>}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>

      {name ? <Combobox.HiddenInput name={name} value={_value} valuesDivider="," /> : null}
    </>
  );
}
